import NoDataDonutChart from "@/components/common/NoDataDonutChart";
import { Skeleton } from "@/components/ui/skeleton";
import InfoTooltip from "@/components/ui/InfoTooltip";
import { WidgetTitle } from "@/components/widgets/WidgetTitle";
import { useDashboard } from "@/contexts/DashboardContext";
import React, { useState } from "react";
import { Cell, Legend, Pie, PieChart, Tooltip as RechartsTooltip, ResponsiveContainer } from "recharts";
import DonutLegendContent from "./DonutLegendContent";
import DonutTooltipContent from "./DonutTooltipContent";

const LEGEND_FONT_SIZE = 14;

export interface DonutChartSegment {
  name: string;
  value: number;
}

interface DonutChartWidgetProps {
  title: string;
  tooltipDescription: string;
  data: DonutChartSegment[];
  colors: string[];
  darkColors: string[];
  loading?: boolean;
  error?: string | null;
  onSegmentClick?: (name: string, value: number) => void;
  legendVerticalAlign?: 'bottom' | 'middle';
  legendScrollable?: boolean;
  legendTruncateAt?: number;
  noDataText?: string;
  /** Override the total shown/used for percentages — defaults to the sum of `data`. Some
   * APIs report a total that includes segments this widget doesn't chart. */
  total?: number;
}

// Direct-tab-standard donut widget: every pie/donut chart in the dashboard
// (Direct tab's Answered/Missed widgets, Queue tab's status distribution and
// calls-handled-by-queue widgets) renders through this one component so
// colors, legend, tooltip and empty/loading/error states stay consistent.
// Each caller keeps its own data-mapping/filter-building logic and only hands
// this component the final {name, value}[] segments to draw.
const DonutChartWidget: React.FC<DonutChartWidgetProps> = ({
  title,
  tooltipDescription,
  data,
  colors,
  darkColors,
  loading,
  error,
  onSegmentClick,
  legendVerticalAlign = 'middle',
  legendScrollable = false,
  legendTruncateAt,
  noDataText = "No call records were found",
  total: totalOverride,
}) => {
  const { theme } = useDashboard();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const currentColors = theme === 'dark' ? darkColors : colors;
  const total = totalOverride ?? data.reduce((sum, item) => sum + item.value, 0);

  const renderTooltipContent = (props: { active?: boolean; payload?: Array<{ payload: DonutChartSegment }> }) => (
    <DonutTooltipContent
      active={props.active}
      payload={props.payload}
      donutData={data}
      currentColors={currentColors}
      total={total}
      fontSize={LEGEND_FONT_SIZE}
    />
  );

  let content: React.ReactNode;
  if (loading) {
    content = <Skeleton className="h-full w-full" />;
  } else if (error || total === 0) {
    content = (
      <div className="flex-1 flex items-center justify-center min-h-[154px]">
        <div className="w-full h-[154px]" style={{ overflow: 'visible' }}>
          <NoDataDonutChart
            width="100%"
            height="100%"
            innerRadius={52}
            outerRadius={68}
            noDataColor="#E5E7EB"
            noDataText={noDataText}
          />
        </div>
      </div>
    );
  } else {
    content = (
      <div className="flex-1 flex items-center justify-center min-h-[154px]">
        <div className="w-full h-[154px]" style={{ overflow: 'visible' }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={52}
                outerRadius={68}
                stroke="none"
                onMouseEnter={(_, index) => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                {data.map((entry, index) => (
                  <Cell
                    key={entry.name}
                    fill={currentColors[index % currentColors.length]}
                    stroke="none"
                    style={{
                      cursor: onSegmentClick && entry.value > 0 ? 'pointer' : 'default',
                      transformOrigin: 'center',
                      transition: 'transform 0.3s cubic-bezier(0.23, 1, 0.32, 1), filter 0.3s ease-out',
                      transform: hoveredIndex === index ? 'scale(1.04)' : 'scale(1)',
                      filter: hoveredIndex === index ? 'drop-shadow(0 6px 16px rgba(0,0,0,0.15))' : 'none',
                      transformBox: 'fill-box',
                      willChange: 'transform, filter',
                    }}
                    onClick={() => onSegmentClick && entry.value > 0 && onSegmentClick(entry.name, entry.value)}
                  />
                ))}
              </Pie>
              <Legend
                layout="vertical"
                verticalAlign={legendVerticalAlign}
                align="right"
                iconType="rect"
                wrapperStyle={{ right: 0, bottom: 0, padding: 0, margin: 0, whiteSpace: 'nowrap', width: 'auto', minWidth: 0, height: '100%', display: 'flex', alignItems: 'center' }}
                content={(
                  <DonutLegendContent
                    donutData={data}
                    currentColors={currentColors}
                    total={total}
                    fontSize={LEGEND_FONT_SIZE}
                    onSegmentClick={onSegmentClick}
                    scrollable={legendScrollable}
                    truncateAt={legendTruncateAt}
                  />
                )}
              />
              <RechartsTooltip content={renderTooltipContent} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-w-[200px] h-full rounded-lg border flex flex-col bg-white dark:bg-[#0C3451] dark:text-white dark:border-[#0C3451] p-3">
      <div className="flex flex-row items-center gap-2">
        <WidgetTitle className="truncate">{title}</WidgetTitle>
        <InfoTooltip content={tooltipDescription} />
      </div>
      <div className="h-full min-h-[154px]">
        {content}
      </div>
    </div>
  );
};

export default DonutChartWidget;
